import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { getLocalizedField, getImageUrl } from '@/utils/helpers';
import ShareButton from '@/components/common/ShareButton';
import { ArrowLeft, BookOpen, Leaf } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecipeDetailPage = () => {
  const { recipeId } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (recipeId) {
      axios.get(`${API}/recipes/${recipeId}`)
        .then(res => setRecipe(res.data))
        .catch(err => {
          console.error('Failed to fetch recipe', err);
          toast.error(t('recipe.notFound'));
          navigate('/');
        })
        .finally(() => setLoading(false));
    }
  }, [recipeId, navigate, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-amber-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <BookOpen className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">{t('recipe.notFound')}</h2>
          <p className="text-amber-600 mb-4">{t('recipe.notFoundDesc')}</p>
          <Button onClick={() => navigate('/')} className="bg-green-600 hover:bg-green-700">{t('common.backToHome')}</Button>
        </Card>
      </div>
    );
  }

  const recipeName = getLocalizedField(recipe, 'name', i18n.language) || recipe.name;
  const recipeDesc = getLocalizedField(recipe, 'description', i18n.language) || recipe.description;
  
  // Get localized ingredients and steps
  const getLocalizedIngredients = () => {
    const lang = i18n.language;
    if (lang === 'en' && recipe.ingredients_en && recipe.ingredients_en.length > 0) {
      return recipe.ingredients_en;
    }
    if (lang === 'ru' && recipe.ingredients_ru && recipe.ingredients_ru.length > 0) {
      return recipe.ingredients_ru;
    }
    return recipe.ingredients || [];
  };
  
  const getLocalizedSteps = () => {
    const lang = i18n.language;
    if (lang === 'en' && recipe.steps_en && recipe.steps_en.length > 0) {
      return recipe.steps_en;
    }
    if (lang === 'ru' && recipe.steps_ru && recipe.steps_ru.length > 0) {
      return recipe.steps_ru;
    }
    return recipe.steps || [];
  };
  
  const localizedIngredients = getLocalizedIngredients();
  const localizedSteps = getLocalizedSteps();

  return (
    <div className="min-h-screen bg-amber-50" data-testid="recipe-detail-page">
      {/* Hero Image */}
      <div className="relative h-[400px] overflow-hidden">
        <img loading="lazy"
          src={getImageUrl(recipe.image_url)} 
          alt={recipeName} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto">
            <Button variant="ghost" className="text-white mb-4" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
            </Button>
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className="bg-white/20 text-white backdrop-blur">
                {t(`recipeCategories.${recipe.category}`, recipe.category)}
              </Badge>
              <Badge className="bg-white/20 text-white backdrop-blur">
                {t(`recipe.dietTypes.${recipe.diet_type}`, recipe.diet_type)}
              </Badge>
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">{recipeName}</h1>
            <p className="text-white/80 text-lg max-w-2xl">{recipeDesc}</p>
            <div className="mt-4">
              <ShareButton
                url={`/recipe/${recipeId}`}
                title={recipeName}
                description={recipeDesc?.substring(0, 200) || ''}
                image={recipe.image_url}
                variant="outline"
                size="sm"
                className="text-white border-white/30 hover:bg-white/20"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Organic Ingredients */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-green-600" />
                  {t('recipe.ingredientTags')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {localizedIngredients && localizedIngredients.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {localizedIngredients.map((ingredient, index) => {
                      // Handle both old string format and new object format
                      const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name;
                      const ingredientLink = typeof ingredient === 'object' ? ingredient.link : null;
                      
                      if (ingredientLink) {
                        return (
                          <a 
                            key={index} 
                            href={ingredientLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm px-3 py-1 border border-green-300 text-green-700 bg-green-50 rounded-full hover:bg-green-100 hover:border-green-400 transition-colors cursor-pointer"
                          >
                            🌿 {ingredientName}
                            <span className="text-blue-500 ml-1">↗</span>
                          </a>
                        );
                      }
                      return (
                        <Badge key={index} variant="outline" className="text-sm px-3 py-1 border-green-300 text-green-700 bg-green-50">
                          🌿 {ingredientName}
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-amber-600 text-sm">{t('recipe.noIngredients')}</p>
                )}
              </CardContent>
            </Card>

            {/* Cooking Steps */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-green-600" />
                  {t('recipe.cookingSteps')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {localizedSteps && localizedSteps.length > 0 ? (
                  <div className="space-y-4">
                    {localizedSteps.map((step, index) => (
                      <div key={index}>
                        <div className="flex gap-4">
                          <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-amber-800">{step}</p>
                          </div>
                        </div>
                        {index < localizedSteps.length - 1 && <Separator className="mt-4" />}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-amber-600 text-sm">{t('recipe.noSteps')}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">{t('recipe.recipeInfo')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-amber-600">{t('recipe.category')}</span>
                  <Badge variant="outline">{t(`recipeCategories.${recipe.category}`, recipe.category)}</Badge>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-amber-600">{t('recipe.difficulty')}</span>
                  <Badge className={`${recipe.difficulty === '简单' ? 'bg-green-100 text-green-700' : recipe.difficulty === '中等' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                    {recipe.difficulty === '简单' ? t('recipe.easy') : recipe.difficulty === '中等' ? t('recipe.medium') : t('recipe.hard')}
                  </Badge>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-amber-600">{t('recipe.dietType')}</span>
                  <Badge variant="outline">{t(`recipe.dietTypes.${recipe.diet_type}`, recipe.diet_type)}</Badge>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                <Button variant="outline" className="w-full">
                  {t('recipe.shareRecipe')}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-amber-500 py-12 mt-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Leaf className="w-6 h-6 text-green-500" />
              <span className="text-white font-bold">{t('siteName')}</span>
            </div>
            <p className="text-sm">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RecipeDetailPage;
